/** Web Push plumbing shared by the hook and app bootstrap. */

export const SERVICE_WORKER_URL = '/sw.js'

export const pushSupported = (): boolean =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window

/** iOS only exposes Notification/PushManager to installed (home-screen) PWAs. */
export const isIos = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone === true
}

/**
 * VAPID keys travel as base64url. `pushManager.subscribe` wants raw bytes.
 *
 * The return type is pinned to `Uint8Array<ArrayBuffer>` rather than plain
 * `Uint8Array`: since TypeScript 5.7 the latter is generic over
 * `ArrayBufferLike`, which includes `SharedArrayBuffer` and so does not
 * satisfy `BufferSource`. Allocating the buffer explicitly makes the backing
 * type provable.
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null

/**
 * Registers the push service worker once per page load. Failure is non-fatal:
 * the portal works fine without notifications.
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration
  if (!pushSupported()) {
    registration = Promise.resolve(null)
    return registration
  }
  registration = navigator.serviceWorker
    .register(SERVICE_WORKER_URL, { scope: '/' })
    .catch((error: unknown) => {
      console.warn('[hermes] service worker registration failed', error)
      return null
    })
  return registration
}
